// Survey UI app
//
//  Copyright (c) 2025 MX8 Inc
//  All rights reserved.
//
//  This software is confidential and proprietary information of MX8.
//  You shall not disclose such Confidential Information and shall use it only
//  in accordance with the terms of the agreement you entered into with MX8.
import { Avatar } from "@mui/material";
import { Box } from "@mui/system";
import React, { FC } from "react";
import { useThemeContext } from "../../../globalContext/global-context";
import { hostThemes } from "../../../theme/hostThemes";
import CommonImageComp from "../../ui/CommonImageComp";
import { headerContainer, headerInfo, headerName } from "./styles";

const ChatHeader: FC<{
  timerImageOPen: boolean;
  progress?: React.ReactNode;
}> = ({ timerImageOPen, progress }): React.ReactElement => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const botIcon = React.useMemo(() => {
    return new URL(theme.assets.header.icon, globalThis.location.origin);
  }, [theme.assets.header.icon]);
  const clientLogo = theme.assets.clientLogo?.trim();
  return (
    <Box data-testid="chat-header" sx={headerContainer(theme, timerImageOPen)}>
      <Box sx={{ display: "flex" }}>
        <Box>
          <Avatar
            data-testid="avatar"
            alt={theme.copy.labels.avatar}
            src={botIcon.href}
            sx={{
              width: hostThemes.chat.header.avatarSize,
              height: hostThemes.chat.header.avatarSize,
            }}
          />
        </Box>
        <Box sx={{ marginLeft: `${hostThemes.chat.header.titleInset}px` }}>
          <Box sx={headerInfo(theme)}>{theme.copy.header.line1}</Box>
          <Box sx={headerName(theme)}>{theme.copy.header.line2}</Box>
        </Box>
      </Box>
      <Box
        data-testid="chat-header-branding"
        sx={{
          alignItems: "center",
          display: "flex",
          gap: `${theme.tokens.spacing["1"]}px`,
          minWidth: 0,
        }}
      >
        {clientLogo && (
          <Box
            data-testid="client-logo-container"
            sx={{
              flexShrink: 1,
              height: hostThemes.chat.header.clientLogoMaxHeight,
              minWidth: 0,
              width: hostThemes.chat.header.clientLogoMaxWidth,
            }}
          >
            <CommonImageComp
              currentVal={{
                image: new URL(clientLogo, globalThis.location.origin).href,
                alt_text: theme.copy.labels.clientLogo,
              }}
              imageTestid="client-logo"
              disablePreviewWrapper
              fillContainer
            />
          </Box>
        )}
        {progress}
      </Box>
    </Box>
  );
};

export default ChatHeader;
